import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Empty,
  Input,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import { useAppCustomers } from '@/hooks/useAppCustomers';
import { useTranslate } from '@/translations';
import { PageContainer, Title, NoAccessAlert } from '@/ui/components';
import {
  Icon,
  faImage,
  faRotateRight,
  faWandMagicSparkles,
} from '@/ui/components/Icon';
import { useProductImageryQuery } from '@/api/ProductImages/hooks';
import {
  ProductImageApiError,
  productImages,
} from '@/api/ProductImages/ProductImages';
import type { ProductImageSummary } from '@/api/ProductImages/types';
import { ImageryContent } from './ProductImages.styled';
import { GenerateImageDialog } from './GenerateImageDialog';
import { PrivateImage } from './PrivateImage';
import { ImageBatchList } from './ImageBatches';
import { ImageQualityTag } from './ImageQualityTag';
import { VariantStatuses } from '@/modules/ProductImages/VariantImage';

export const imageryRoot = '/product-images';
export const positivePage = (value: string | null) =>
  Math.max(1, Number(value) || 1);
export const activeGeneration = (status: string) =>
  [
    'Pending',
    'Queued',
    'Running',
    'Generating',
    'Researching',
    'Evaluating',
    'Publishing',
  ].includes(status);
export const knownStatuses = [
  'Queued',
  'Running',
  'Published',
  'Rejected',
  'NeedsReview',
  'Failed',
  'Unknown',
  'Stopped',
  'Skipped',
  'Deferred',
];

export const ImageryStatus = ({ status }: { status?: string | null }) => (
  <Tag
    color={
      status === 'Published' || status === 'Succeeded'
        ? 'success'
        : status === 'Failed' || status === 'Rejected'
          ? 'error'
          : status === 'Unknown' ||
              status === 'Deferred' ||
              status === 'Stopped'
            ? 'warning'
            : status && activeGeneration(status)
              ? 'processing'
              : 'default'
    }
  >
    {status || 'Not generated'}
  </Tag>
);

export const ImageryError = ({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) => {
  const { t } = useTranslate();
  if (error instanceof ProductImageApiError && error.status === 403)
    return <NoAccessAlert />;
  return (
    <Alert
      type="error"
      showIcon
      message={t(
        error instanceof ProductImageApiError && error.status === 404
          ? 'imagery.apiUnavailable'
          : 'imagery.loadError',
      )}
      action={
        retry ? (
          <Button onClick={retry}>{t('imagery.retry')}</Button>
        ) : undefined
      }
    />
  );
};

export const GenerationButton = ({
  product,
  onClick,
}: {
  product: ProductImageSummary;
  onClick: () => void;
}) => {
  const { t } = useTranslate();
  const active = activeGeneration(product.generationStatus);
  return (
    <Tooltip
      title={
        !product.canGenerate && !active ? product.unavailableReason : undefined
      }
    >
      <Button
        style={{ width: 136 }}
        disabled={active || !product.canGenerate}
        icon={
          <Icon
            icon={product.currentImage ? faRotateRight : faWandMagicSparkles}
          />
        }
        onClick={onClick}
      >
        {t(
          active
            ? 'imagery.generating'
            : product.currentImage
              ? 'imagery.regenerate'
              : 'imagery.generate',
        )}
      </Button>
    </Tooltip>
  );
};

export const ImageryAccess = ({ children }: { children: React.ReactNode }) => {
  const { userPermissions, hasGbaAdminAccess } = useAppCustomers();
  return !userPermissions.loaded ? (
    <Spin />
  ) : hasGbaAdminAccess ? (
    <>{children}</>
  ) : (
    <NoAccessAlert />
  );
};

const ProductList = () => {
  const { t } = useTranslate();
  const { userPermissions, customerIds } = useAppCustomers();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<ProductImageSummary | null>(null);
  const query = {
    page: positivePage(params.get('productPage')),
    pageSize: positivePage(params.get('productSize') || '25'),
    search: params.get('search') || undefined,
    status: params.get('productStatus') || undefined,
    customerId: Number(params.get('customer')) || undefined,
  };
  const result = useProductImageryQuery(['products', query], () =>
    productImages.products(query),
  );
  const change = (values: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key),
    );
    setParams(next);
  };
  if (result.isError)
    return (
      <ImageryError error={result.error} retry={() => void result.refetch()} />
    );
  return (
    <>
      <div className="imagery-toolbar">
        <Input.Search
          aria-label={t('imagery.search')}
          placeholder={t('imagery.search')}
          allowClear
          defaultValue={query.search}
          onSearch={(value) =>
            change({ search: value, productPage: undefined })
          }
        />
        <Select
          aria-label={t('imagery.customer')}
          placeholder={t('imagery.allCustomers')}
          allowClear
          value={query.customerId}
          onChange={(value) =>
            change({ customer: value?.toString(), productPage: undefined })
          }
          options={(userPermissions.customers ?? [])
            .filter((customer) => customerIds.includes(customer.customerId))
            .map((customer) => ({
              value: customer.customerId,
              label: customer.customerName,
            }))}
        />
        <Select
          aria-label={t('imagery.status')}
          placeholder={t('imagery.allStatuses')}
          allowClear
          value={query.status}
          onChange={(value) =>
            change({ productStatus: value, productPage: undefined })
          }
          options={['BelowThreshold', ...knownStatuses].map((value) => ({
            value,
            label:
              value === 'BelowThreshold' ? t('imagery.belowThreshold') : value,
          }))}
        />
        <Tooltip title={t('imagery.refresh')}>
          <Button
            aria-label={t('imagery.refresh')}
            icon={<Icon icon={faRotateRight} />}
            onClick={() => void result.refetch()}
            loading={result.isFetching}
          />
        </Tooltip>
      </div>
      <Table<ProductImageSummary>
        size="middle"
        rowKey="productId"
        loading={result.isLoading}
        dataSource={result.data?.items ?? []}
        scroll={{ x: 1100 }}
        locale={{ emptyText: <Empty description={t('imagery.noRecords')} /> }}
        pagination={{
          current: query.page,
          pageSize: query.pageSize,
          total: result.data?.totalCount ?? 0,
          showSizeChanger: true,
          onChange: (page, size) =>
            change({
              productPage: page.toString(),
              productSize: size.toString(),
            }),
        }}
        columns={[
          {
            title: t('imagery.currentImage'),
            width: 135,
            render: (_, product) =>
              product.currentImage?.sourceKind === 'Generated' ? (
                <div className="imagery-image-output">
                  <PrivateImage
                    src={
                      product.currentImage.thumbnailUrl ??
                      product.currentImage.contentUrl
                    }
                    assetId={product.currentImage.assetId}
                    thumbnail
                    label={t(
                      product.currentImage.representationKind === 'Outline'
                        ? 'imagery.outlineImage'
                        : 'imagery.currentImage',
                    )}
                  />
                  <span className="imagery-caption">
                    {t(
                      product.currentImage.representationKind === 'Outline'
                        ? 'imagery.outlineImage'
                        : 'imagery.currentImage',
                    )}
                  </span>
                  <ImageQualityTag image={product.currentImage} />
                </div>
              ) : (
                <span className="text-secondary">
                  {t(
                    product.currentImage
                      ? 'imagery.unsupportedSource'
                      : 'imagery.noImage',
                  )}
                </span>
              ),
          },
          {
            title: t('imagery.product'),
            width: 260,
            render: (_, product) => (
              <>
                <Link
                  to={`${imageryRoot}/products/${product.productId}?${params}`}
                >
                  {product.productCode}
                </Link>
                <div>{product.productDescription}</div>
                <small className="text-secondary">
                  {product.manufacturerName}
                </small>
              </>
            ),
          },
          {
            title: t('imagery.customer'),
            dataIndex: 'customerName',
            width: 160,
          },
          {
            title: t('imagery.qualifyingStock'),
            dataIndex: 'qualifyingStockCount',
            width: 130,
          },
          {
            title: t('imagery.status'),
            width: 160,
            render: (_, product) => (
              <ImageryStatus status={product.generationStatus} />
            ),
          },
          {
            title: t('imagery.variantResults'),
            width: 150,
            render: (_, product) => (
              <VariantStatuses image={product.currentImage} />
            ),
          },
          {
            title: t('imagery.actions'),
            width: 165,
            render: (_, product) => (
              <GenerationButton
                product={product}
                onClick={() => setSelected(product)}
              />
            ),
          },
        ]}
      />
      {selected ? (
        <GenerateImageDialog
          product={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  );
};

export const ProductImagesPage = () => {
  const { t } = useTranslate();
  const [params, setParams] = useSearchParams();
  return (
    <ImageryAccess>
      <PageContainer>
        <ImageryContent>
          <Title title={t('imagery.title')} icon={faImage} />
          <Tabs
            activeKey={params.get('tab') === 'batches' ? 'batches' : 'products'}
            onChange={(tab) => {
              const next = new URLSearchParams(params);
              next.set('tab', tab);
              setParams(next);
            }}
            items={[
              {
                key: 'products',
                label: t('imagery.products'),
                children: <ProductList />,
              },
              {
                key: 'batches',
                label: t('imagery.batches'),
                children: <ImageBatchList />,
              },
            ]}
            destroyOnHidden
          />
        </ImageryContent>
      </PageContainer>
    </ImageryAccess>
  );
};
